/**
 * Sound Helper Utility
 * Manages audio playback for auction events
 * 
 * Place sound files in: public/sounds/
 * Supported formats: .mp3, .wav, .ogg
 */

class SoundHelper {
  constructor() {
    this.audioCache = new Map();
    this.isEnabled = true;
    this.volume = 0.7; // Default volume (0.0 to 1.0)
    this.soundQueue = []; // Queue for sounds to play sequentially
    this.isPlaying = false; // Flag to track if a sound is currently playing
    this.currentAudio = null; // Reference to currently playing audio
    this.recentlyPlayed = new Map(); // Track recently played sounds to prevent duplicates (soundName -> timestamp)
    this.duplicatePreventionWindow = 2000; // Prevent same sound within 2 seconds
  }

  /**
   * Enable or disable sound playback
   */
  setEnabled(enabled) {
    this.isEnabled = enabled;
  }

  /**
   * Set volume for all sounds (0.0 to 1.0)
   */
  setVolume(volume) {
    this.volume = Math.max(0, Math.min(1, volume));
    // Update volume for all cached audio elements
    this.audioCache.forEach((audio) => {
      audio.volume = this.volume;
    });
  }

  /**
   * Get current volume
   */
  getVolume() {
    return this.volume;
  }

  /**
   * Check if sound is enabled
   */
  isSoundEnabled() {
    return this.isEnabled;
  }

  /**
   * Preload a sound file
   */
  preloadSound(soundName) {
    if (this.audioCache.has(soundName)) {
      return; // Already loaded
    }

    const audio = new Audio(`/sounds/${soundName}`);
    audio.volume = this.volume;
    audio.preload = "auto";
    
    // Handle loading errors gracefully
    audio.addEventListener("error", (e) => {
      console.warn(`Failed to load sound: ${soundName}`, e);
    });

    this.audioCache.set(soundName, audio);
  }

  /**
   * Play a sound file (queued if another sound is playing)
   * @param {string} soundName - Name of the sound file (e.g., "timer-reset.mp3")
   * @param {object} options - Optional playback options
   */
  playSound(soundName, options = {}) {
    if (!this.isEnabled) {
      return;
    }

    // Check if this sound was recently played (prevent duplicates)
    const now = Date.now();
    const lastPlayed = this.recentlyPlayed.get(soundName);
    if (lastPlayed && (now - lastPlayed) < this.duplicatePreventionWindow) {
      console.log(`Skipping duplicate sound: ${soundName} (played ${now - lastPlayed}ms ago)`);
      return; // Skip if played recently
    }

    // Check if sound is already in queue
    const isInQueue = this.soundQueue.some(item => item.soundName === soundName);
    if (isInQueue) {
      console.log(`Skipping duplicate sound in queue: ${soundName}`);
      return; // Skip if already queued
    }

    // Mark as recently played
    this.recentlyPlayed.set(soundName, now);

    // Add to queue
    this.soundQueue.push({ soundName, options });

    // If not currently playing, start processing the queue
    if (!this.isPlaying) {
      this.processQueue();
    }
  }

  /**
   * Process the sound queue, playing sounds one after another
   */
  processQueue() {
    if (this.soundQueue.length === 0) {
      this.isPlaying = false;
      this.currentAudio = null;
      return;
    }

    this.isPlaying = true;
    const { soundName, options } = this.soundQueue.shift();

    try {
      // Always create a new Audio instance for independent playback
      // This allows multiple sounds to be queued and played sequentially
      const audio = new Audio(`/sounds/${soundName}`);
      
      // Preload if not already cached (for faster loading)
      if (!this.audioCache.has(soundName)) {
        const preloadAudio = new Audio(`/sounds/${soundName}`);
        preloadAudio.preload = "auto";
        this.audioCache.set(soundName, preloadAudio);
      }

      // Set initial volume
      audio.volume = options?.volume ?? this.volume;
      audio.loop = options?.loop ?? false;

      // Handle errors
      audio.addEventListener("error", (e) => {
        console.warn(`Failed to play sound: ${soundName}`, e);
        if (options?.onError) {
          options.onError(new Error(`Failed to play sound: ${soundName}`));
        }
        // Continue to next sound in queue even if this one fails
        this.processQueue();
      });

      // Set options
      if (options?.volume !== undefined) {
        audio.volume = Math.max(0, Math.min(1, options.volume));
      }
      if (options?.loop !== undefined) {
        audio.loop = options.loop;
      }

      // Store reference to current audio
      this.currentAudio = audio;

      // Handle playback end - play next sound in queue
      audio.addEventListener("ended", () => {
        if (options?.onEnded) {
          options.onEnded();
        }
        // Clean up old entries from recentlyPlayed (older than 5 seconds)
        const cleanupTime = Date.now() - 5000;
        for (const [key, timestamp] of this.recentlyPlayed.entries()) {
          if (timestamp < cleanupTime) {
            this.recentlyPlayed.delete(key);
          }
        }
        // Play next sound in queue
        this.processQueue();
      }, { once: true });

      // Play the sound
      const playPromise = audio.play();
      
      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          // Auto-play was prevented (user interaction required)
          console.warn(`Sound playback prevented: ${soundName}`, error);
          if (options?.onError) {
            options.onError(error);
          }
          // Continue to next sound in queue even if this one fails
          this.processQueue();
        });
      }
    } catch (error) {
      console.error(`Error playing sound: ${soundName}`, error);
      if (options?.onError) {
        options.onError(error);
      }
      // Continue to next sound in queue even if this one fails
      this.processQueue();
    }
  }

  /**
   * Stop a currently playing sound
   */
  stopSound(soundName) {
    const audio = this.audioCache.get(soundName);
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
  }

  /**
   * Stop all currently playing sounds
   */
  stopAllSounds() {
    // Stop current audio if playing
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
      this.currentAudio = null;
    }

    // Stop all cached audio
    this.audioCache.forEach((audio) => {
      audio.pause();
      audio.currentTime = 0;
    });

    // Clear the queue
    this.soundQueue = [];
    this.isPlaying = false;
    // Clear recently played tracking
    this.recentlyPlayed.clear();
  }

  /**
   * Preload multiple sounds at once
   */
  preloadSounds(soundNames) {
    soundNames.forEach((soundName) => {
      this.preloadSound(soundName);
    });
  }

  /**
   * Clear audio cache (useful for cleanup)
   */
  clearCache() {
    this.stopAllSounds();
    this.audioCache.clear();
  }
}

// Export singleton instance
export const soundHelper = new SoundHelper();

// Export class for testing
export default SoundHelper;
